import * as React from 'react'
import { PexIcon } from '../icons/PexIcon'
import { cn } from '../../lib/utils'

export interface FilterMenuProps {
  /** Trigger text, e.g. "Store". */
  label: string
  /** Selected values behind this menu; shown as a count beside the label. */
  activeCount?: number
  /** Popover width utility; menus with long option labels ask for more room. */
  widthClassName?: string
  /** Popover body. Receives `close` so Apply / Cancel buttons can dismiss it. */
  children: (close: () => void) => React.ReactNode
  /** Fired whenever the popover opens, e.g. to reset a draft to the live value. */
  onOpen?: () => void
}

/**
 * ShipStation-style "Filter by" trigger: a quiet text button that opens a
 * popover under it. Closes on an outside pointer-down and on Escape, returning
 * focus to the trigger — the same contract as DateRangePicker.
 */
export function FilterMenu({
  label,
  activeCount = 0,
  widthClassName = 'w-64',
  children,
  onOpen,
}: FilterMenuProps) {
  const [open, setOpen] = React.useState(false)
  const rootRef = React.useRef<HTMLDivElement>(null)
  const id = React.useId()
  const triggerId = `${id}-trigger`

  // Looked up by id, not a ref: `close` is handed to the render-prop body, and a
  // ref read reachable from render is exactly what the React compiler forbids.
  const close = React.useCallback(() => {
    setOpen(false)
    document.getElementById(triggerId)?.focus()
  }, [triggerId])

  React.useEffect(() => {
    if (!open) return
    const onPointerDown = (event: PointerEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false)
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') close()
    }
    document.addEventListener('pointerdown', onPointerDown)
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('pointerdown', onPointerDown)
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [close, open])

  const active = activeCount > 0

  return (
    <div ref={rootRef} className="relative">
      <button
        id={triggerId}
        type="button"
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-controls={open ? id : undefined}
        onClick={() => {
          if (!open) onOpen?.()
          setOpen((value) => !value)
        }}
        className={cn(
          'inline-flex h-8 items-center gap-1 rounded-[var(--radius-control)] px-2 text-sm transition-colors hover:bg-[var(--color-surface-muted)] focus-visible:outline-none focus-visible:shadow-[var(--shadow-focus)]',
          active || open
            ? 'font-medium text-[var(--color-text)]'
            : 'text-[var(--color-text-muted)]',
        )}
      >
        {label}
        {active && (
          <span className="inline-flex min-w-[1.25rem] items-center justify-center rounded-full bg-[var(--color-selected)] px-1.5 text-[11px] font-semibold tabular-nums text-[var(--color-text)]">
            {activeCount}
          </span>
        )}
        <PexIcon name="caretDown" size="xs" className={open ? 'rotate-180' : undefined} />
      </button>
      {open && (
        <div
          id={id}
          role="dialog"
          aria-label={label}
          className={cn(
            'absolute left-0 top-full z-[var(--z-dropdown)] mt-1 max-w-[calc(100vw-1.5rem)] overflow-hidden rounded-[var(--surface-lifted-radius)] border border-[var(--surface-lifted-border)] bg-[var(--surface-lifted-bg)] shadow-[var(--surface-lifted-shadow)]',
            widthClassName,
          )}
        >
          {children(close)}
        </div>
      )}
    </div>
  )
}

/** The "Remove filters" row at the top of a checkbox menu. */
export function FilterMenuClear({
  label,
  onClear,
  disabled,
}: {
  label: string
  onClear: () => void
  disabled?: boolean
}) {
  return (
    <button
      type="button"
      onClick={onClear}
      disabled={disabled}
      className="flex w-full items-center gap-2 border-b border-[var(--color-border)] px-3 py-2 text-left text-sm text-[var(--color-text-muted)] hover:bg-[var(--color-surface-muted)] disabled:cursor-default disabled:opacity-60 disabled:hover:bg-transparent"
    >
      <PexIcon name="close" size="xs" />
      {label}
    </button>
  )
}

/** One checkbox row; applies immediately, like ShipStation's multi-select menus. */
export function FilterMenuCheckbox({
  checked,
  onChange,
  children,
}: {
  checked: boolean
  onChange: (checked: boolean) => void
  children: React.ReactNode
}) {
  return (
    <label className="flex cursor-pointer items-center gap-2.5 border-b border-[var(--color-border)] px-3 py-2 text-sm text-[var(--color-text)] last:border-b-0 hover:bg-[var(--color-surface-muted)]">
      <input
        type="checkbox"
        checked={checked}
        onChange={(event) => onChange(event.target.checked)}
        className="h-4 w-4 shrink-0 cursor-pointer"
      />
      <span className="min-w-0 flex-1 truncate">{children}</span>
    </label>
  )
}
